import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, TextField, Autocomplete,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import SidebarLayout from '../components/SidebarLayout';

interface Stats {
  total: number;
  success_count: number;
  failed_count: number;
  today_count: number;
}

interface PlayRecord {
  id: number;
  client_id: string;
  status: number;
  execution_time: string;
  video_url: string;
  video_title?: string;
  play_seconds: number;
  page_view?: number | null;
  kind?: string;
  stat_video_id?: number | null;
  user?: { username: string };
}

interface UserItem {
  id: number;
  username: string;
}

interface LatestView {
  userId: number;
  username: string;
  clientId: string;
  statVideoUrl: string;
  viewStart: number | null;
  currentView: number | null;
  delta: number | null;
  endedAt: string;
}

const PlayDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats>({ total: 0, success_count: 0, failed_count: 0, today_count: 0 });
  const [records, setRecords] = useState<PlayRecord[]>([]);
  const [latestViews, setLatestViews] = useState<LatestView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState<UserItem[]>([]);
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const filterParams = useCallback(() => ({
    userId: selectedUser || undefined,
    status: status === '' ? undefined : status,
    kind: kind === '' ? undefined : kind,
    startTime: startTime || undefined,
    endTime: endTime || undefined,
  }), [selectedUser, status, kind, startTime, endTime]);

  const fetchUsers = async (search = '') => {
    const resp = await axios.get('/api/users', { params: { search } });
    setUsers(resp.data.users || []);
  };

  const fetchStats = useCallback(async () => {
    const resp = await axios.get('/api/bili/play-statistics', { params: filterParams() });
    setStats(resp.data);
  }, [filterParams]);

  const fetchRecords = useCallback(async () => {
    const resp = await axios.get('/api/bili/play-records', {
      params: {
        ...filterParams(),
        page: page + 1,
        limit: rowsPerPage,
      },
    });
    setRecords(resp.data.records || []);
    setTotal(resp.data.total || 0);
  }, [filterParams, page, rowsPerPage]);

  const fetchLatestViews = useCallback(async () => {
    const resp = await axios.get('/api/bili/view-sessions/latest-by-user', {
      params: { userId: selectedUser || undefined },
    });
    setLatestViews(resp.data.items || []);
  }, [selectedUser]);

  useEffect(() => {
    if (user?.role === 'admin') fetchUsers().catch(console.error);
  }, [user]);

  useEffect(() => { fetchStats().catch(console.error); }, [fetchStats]);
  useEffect(() => { fetchRecords().catch(console.error); }, [fetchRecords]);
  useEffect(() => { fetchLatestViews().catch(console.error); }, [fetchLatestViews]);

  const formatTime = (t?: string) => {
    if (!t) return '-';
    return new Date(t).toLocaleString('zh-CN', { hour12: false });
  };

  return (
    <SidebarLayout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>视频播放量统计</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            ['今日播放', stats.today_count, 'text.primary'],
            ['总次数', stats.total, 'text.primary'],
            ['成功', stats.success_count, 'primary.main'],
            ['失败', stats.failed_count, 'error.main'],
          ].map(([label, value, color]) => (
            <Grid item xs={6} sm={3} key={label as string}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="h4" color={color as string}>{value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {latestViews.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>小视频当前播放量（按用户）</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>用户</TableCell>
                    <TableCell>客户端</TableCell>
                    <TableCell>统计小视频</TableCell>
                    <TableCell>开始播放量</TableCell>
                    <TableCell>当前播放量</TableCell>
                    <TableCell>增量</TableCell>
                    <TableCell>采样时间</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {latestViews.map((v) => (
                    <TableRow key={`${v.userId}-${v.clientId}-${v.endedAt}`}>
                      <TableCell>{v.username || v.userId}</TableCell>
                      <TableCell>{v.clientId}</TableCell>
                      <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v.statVideoUrl}>
                        {v.statVideoUrl || '-'}
                      </TableCell>
                      <TableCell>{v.viewStart ?? '-'}</TableCell>
                      <TableCell>
                        <Typography component="span" fontWeight={600}>{v.currentView ?? '-'}</Typography>
                      </TableCell>
                      <TableCell>{v.delta != null ? (v.delta >= 0 ? `+${v.delta}` : v.delta) : '-'}</TableCell>
                      <TableCell>{formatTime(v.endedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {user?.role === 'admin' && (
            <Grid item xs={12} md={2.5}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.username}
                value={users.find((u) => u.id === selectedUser) || null}
                onChange={(_, newValue) => {
                  setSelectedUser(newValue ? newValue.id : '');
                  setPage(0);
                }}
                inputValue={userSearch}
                onInputChange={(_, newInputValue) => {
                  setUserSearch(newInputValue);
                  fetchUsers(newInputValue).catch(console.error);
                }}
                renderInput={(params) => <TextField {...params} label="选择用户" variant="outlined" fullWidth />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                clearOnBlur={false}
              />
            </Grid>
          )}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>类型</InputLabel>
              <Select
                value={kind}
                label="类型"
                onChange={(e) => { setKind(e.target.value); setPage(0); }}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="normal">普通轮播</MenuItem>
                <MenuItem value="stat">统计小视频</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>状态</InputLabel>
              <Select
                value={status}
                label="状态"
                onChange={(e) => { setStatus(e.target.value); setPage(0); }}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="1">成功</MenuItem>
                <MenuItem value="0">失败</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              label="开始时间"
              type="datetime-local"
              value={startTime}
              onChange={(e) => { setStartTime(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              label="结束时间"
              type="datetime-local"
              value={endTime}
              onChange={(e) => { setEndTime(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
        </Grid>

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>时间</TableCell>
                  <TableCell>操作人</TableCell>
                  <TableCell>客户端</TableCell>
                  <TableCell>类型</TableCell>
                  <TableCell>视频标题</TableCell>
                  <TableCell>视频地址</TableCell>
                  <TableCell>播放秒数</TableCell>
                  <TableCell>页面播放量</TableCell>
                  <TableCell>状态</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>暂无记录</TableCell>
                  </TableRow>
                ) : records.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{formatTime(r.execution_time)}</TableCell>
                    <TableCell>{r.user?.username || '-'}</TableCell>
                    <TableCell>{r.client_id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.kind === 'stat' ? '统计小视频' : '普通轮播'}
                        color={r.kind === 'stat' ? 'secondary' : 'default'}
                        variant="outlined"
                      />
                      {r.kind === 'stat' && r.stat_video_id != null && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          ID #{r.stat_video_id}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.video_title || ''}>
                      {r.video_title || '-'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.video_url}>
                      <Box component="a" href={r.video_url} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                        {r.video_url}
                      </Box>
                    </TableCell>
                    <TableCell>{r.play_seconds}s</TableCell>
                    <TableCell>{r.page_view != null ? r.page_view : '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status === 1 ? '成功' : '失败'}
                        color={r.status === 1 ? 'success' : 'error'} variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={total} page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="每页行数"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        </Paper>
      </Container>
    </SidebarLayout>
  );
};

export default PlayDashboard;
